export interface ActivityRom {
    key: string;
    label: string;
}

export interface ActivityGoal {
    key: string;
    label: string;
}

export interface ActivityEvent {
    key: string;
    label: string;
    format?: string;
}

export interface ActivityDefinition {
    id: string;
    name: string;
    type: "Coaching" | "Movement" | "Meditation";
    expectedGoals?: ActivityGoal[];
    expectedEvents?: ActivityEvent[];
    expectedRoms?: ActivityRom[];
    ignoredGoals?: string[];
    ignoredRoms?: string[];
    ignoredEvents?: string[];
}

const safeFloat = (value: any): number | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
};

export const EVENT_FORMATTERS: Record<string, (events: any[]) => any> = {
    eventCount: (events) => events.length,
    eventEnabled: (events) => {
        if (events.length === 0 || events[0].value === null) {
            return "-";
        }
        return events[0].value === "true" ? "Enabled" : "Disabled";
    },
    eventInterval: (events) => {
        if (events.length === 0 || !events[0].interval) {
            return "-";
        }
        const intervalStr = events[0].interval;
        const parts = intervalStr.split(":").map(p => parseInt(p, 10));
        if (parts.length !== 3 || parts.some(isNaN)) {
            return intervalStr;
        }

        const [h, m, s] = parts;
        if (h === 0 && m === 0 && s === 0) {
            return "-";
        }

        const resultParts = [];
        if (h > 0) {
            resultParts.push(`${h}H`);
        }
        if (m > 0) {
            resultParts.push(`${m}m`);
        }
        if (s > 0) {
            resultParts.push(`${s}s`);
        }
        return resultParts.join(" ");
    },
    eventValue: (events) => (events.length > 0 && events[0].value !== null) ? events[0].value : "-",
    eventValueFirst: (events) => (events.length > 0 && events[0].value !== null) ? events[0].value : "-",
    eventValueNone: (_events) => "",
    eventPercentage: (events) => {
        if (events.length === 0 || events[0].value === null) {
            return "-";
        }
        const num = safeFloat(events[0].value);
        return num !== null ? `${(num * 100).toFixed(0)}%` : "-";
    },
    eventSeconds: (events) => {
        if (events.length === 0 || events[0].value === null) {
            return "-";
        }
        const num = safeFloat(events[0].value);
        return num !== null ? `${num.toFixed(2)}s` : "-";
    }
};

export const CUSTOM_EVENT_FORMATTERS: Record<string, (events: any[]) => any> = {
    rulingResultsFormatter: (events) => {
        const results = events.find(e => e.type === "RulingInOut__RESULTS");
        if (!results?.value) {
            return "Available";
        }

        const data = results.value;

        // If it's not a string, return as-is
        if (typeof data !== "string") {
            return String(data);
        }

        // Parse the bracketed format: [question, answer][question, answer]...
        const regex = /\[([^,]+),\s*(\d+)\]/g;
        const matches = [];
        let match;

        while ((match = regex.exec(data)) !== null) {
            matches.push({
                question: match[1].trim(),
                answer: parseInt(match[2], 10)
            });
        }

        if (matches.length === 0) {
            return String(data);
        }

        // Return JSX element with formatted question-answer pairs
        return (
            <div className="flex flex-col gap-1">
                {
                    matches.map(({ question, answer }, index) => {
                    // Add question mark if missing
                        let formattedQuestion = question;
                        if (!formattedQuestion.endsWith("?")) {
                            formattedQuestion += "?";
                        }

                        // Convert 0/1 values to No/Yes
                        const formattedAnswer = answer === 0 ? "No" : answer === 1 ? "Yes" : String(answer);

                        return (
                            <div key={index} className="text-gray-700 text-sm font-medium leading-5">
                                {formattedQuestion} <span className="font-bold">{formattedAnswer}</span>
                            </div>
                        );
                    })
                }
            </div>
        );
    }
};

export const UNEXPECTED_GOAL_LABEL_MAP: Record<string, string> = {};

export const UNEXPECTED_ACTIVITY_EVENTS: ActivityEvent[] = [
    { key: "ACTIVITY_PAUSED", label: "Activity Paused", format: "eventValueNone" },
    { key: "ACTIVITY_RESUMED", label: "Activity Resumed", format: "eventInterval" },
    { key: "AFFECTED_ARM", label: "Affected Arm", format: "eventValueFirst" },
    { key: "Augmentation__STRENGTH_CHANGED", label: "Augmentation Strength Changed", format: "eventValue" },
    { key: "CHARACTER_CHANGED", label: "Character Changed", format: "eventValue" },
    { key: "MIRRORED_TIME", label: "Mirrored Time", format: "eventValue" },
    { key: "POSE", label: "Position", format: "eventValueFirst" },
    { key: "RESULTS", label: "Results", format: "eventValue" },
    { key: "SET_MIRRORING", label: "Set Mirroring", format: "eventValue" },
    { key: "SKIP", label: "Skips", format: "eventCount" },
    { key: "TUTORIAL_COMPLETED", label: "Tutorial Completed", format: "eventInterval" },
    { key: "TUTORIAL_STARTED", label: "Tutorial Started", format: "eventValueNone" }
];

export const ACTIVITY_DEFINITIONS: Record<string, ActivityDefinition> = {
    "Affirmations": { // AG Complete
        id: "Affirmations",
        name: "Affirmations",
        type: "Coaching",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "BallRoller": { // AG Complete
        id: "BallRoller",
        name: "BallRoller",
        type: "Movement",
        expectedGoals: [
            { key: "Zones Complete", label: "Zones Completed" }
        ]
    },
    "BowAndArrow": {  // Verify
        id: "BowAndArrow",
        name: "Bow and Arrow",
        type: "Movement",
        expectedGoals: [
            { key: "Arrows Grabbed", label: "Arrows Grabbed" }, // Missing from AG
            { key: "Target Hit", label: "Targets Hit" }, // Missing from AG
            { key: "Hidden Targets", label: "Hidden Targets" } // Missing from AG
        ],
        expectedEvents: [
            { key: "AFFECTED_ARM", label: "Affected Arm", format: "eventValueFirst" }
        ]
    },
    "Breathing": { // AG Complete
        id: "Breathing",
        name: "Breathing",
        type: "Meditation"
    },
    "BringItOn": {  // AG Complete
        id: "BringItOn",
        name: "Bring It On",
        type: "Coaching"
    },
    "CalibrationUpperExtremity": { // AG Complete
        id: "CalibrationUpperExtremity",
        name: "Calibration",
        type: "Movement",
        expectedRoms: [
            { key: "LeftShoulderAbduction", label: "Left Shoulder Abduction" },
            { key: "LeftShoulderFlexion", label: "Left Shoulder Flexion" },
            { key: "LeftShoulderScaption", label: "Left Shoulder Scaption" },
            { key: "RightShoulderAbduction", label: "Right Shoulder Abduction" },
            { key: "RightShoulderFlexion", label: "Right Shoulder Flexion" },
            { key: "RightShoulderScaption", label: "Right Shoulder Scaption" }
        ],
        expectedGoals: [
            { key: "Checkpoints Hit", label: "Checkpoints Hit" },
            { key: "Perfect Motion", label: "Perfect Motions" },
            { key: "Improved Ranges", label: "Improved Ranges" }
        ],
        expectedEvents: [
            { key: "SKIP", label: "Motions Skipped", format: "eventCount" },
            { key: "AFFECTED_ARM", label: "Affected Arm", format: "eventValueFirst" }
        ]
    },
    "ConnectTheDots": { // Verify
        id: "ConnectTheDots",
        name: "Connect The Dots",
        type: "Movement",
        expectedGoals: [
            { key: "Shapes Completed", label: "Shapes Completed" }
        ],
        expectedEvents: [
            { key: "ConnectTheDots__SHAPE_STARTED", label: "Shapes Started", format: "eventCount" }, // Missing from AG
            { key: "ConnectTheDots__SHAPE_SKIPPED", label: "Shapes Skipped", format: "eventCount" }, // Missing from AG
            { key: "ConnectTheDots__SHAPE_FAILED", label: "Shapes Failed", format: "eventCount" }, // Missing from AG
            { key: "AFFECTED_ARM", label: "Affected Arm", format: "eventValueFirst" }
        ],
        ignoredEvents: [
            "ConnectTheDots__SHAPE_COMPLETE", // Handled by 'Shapes Completed' goal
            "SKIP" // Handled by ConnectTheDots__SHAPE_SKIPPED event
        ]
    },
    "ConversationWithControl": { // Verify
        id: "ConversationWithControl",
        name: "Conversation With Control",
        type: "Coaching",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" },
            { key: "IFS__THOUGHT_BUBBLE_USES", label: "Thought Bubble Uses", format: "eventCount" } // Verify
        ]
    },
    "ConversationWithYourFears": { // Verify
        id: "ConversationWithYourFears",
        name: "Conversation With Your Fears",
        type: "Coaching",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" },
            { key: "IFS__THOUGHT_BUBBLE_USES", label: "Thought Bubble Uses", format: "eventCount" } // Verify
        ]
    },
    "ConversationWithYourPain": { // Verify
        id: "ConversationWithYourPain",
        name: "Conversation With Your Pain",
        type: "Coaching",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" },
            { key: "IFS__THOUGHT_BUBBLE_USES", label: "Thought Bubble Uses", format: "eventCount" } // Verify
        ]
    },
    "DishStacking": { // AG Complete
        id: "DishStacking",
        name: "Dish Stacking",
        type: "Movement",
        expectedGoals: [
            { key: "Dishes Stacked", label: "Dishes Stacked" }
        ],
        expectedEvents: [
            { key: "SKIP", label: "Dishes Skipped", format: "eventCount" }
        ]
    },
    "DogFetchHome": { // Verify
        id: "DogFetchHome",
        name: "Dog Fetch",
        type: "Movement",
        expectedGoals: [
            { key: "Ball Grabbed", label: "Balls Grabbed" },
            { key: "Target Hit", label: "Targets Hit" }
        ]
    },
    "ExplorationWithFighting": { // Verify
        id: "ExplorationWithFighting",
        name: "Exploration with Fighting",
        type: "Coaching",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" },
            { key: "IFS__THOUGHT_BUBBLE_USES", label: "Thought Bubble Uses", format: "eventCount" } // Verify
        ]
    },
    "ExploringFrustration": { // Verify
        id: "ExploringFrustration",
        name: "Exploring Frustration",
        type: "Coaching",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" },
            { key: "IFS__THOUGHT_BUBBLE_USES", label: "Thought Bubble Uses", format: "eventCount" } // Verify
        ]
    },
    "ExploringTheNeedToFix": { // Verify
        id: "ExploringTheNeedToFix",
        name: "Exploring the Need to Fix",
        type: "Coaching",
        expectedEvents: [
            { key: "IFS__THOUGHT_BUBBLE_USES", label: "Thought Bubble Uses", format: "eventCount" }, // Verify
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "FlowerGarden": { // AG Complete
        id: "FlowerGarden",
        name: "Flower Garden",
        type: "Movement",
        expectedRoms: [
            { key: "BackFlexion", label: "Lower Back Flexion" }
        ],
        expectedGoals: [
            { key: "Flowers Grabbed", label: "Flowers Grabbed" }
        ],
        expectedEvents: [
            { key: "SKIP", label: "Flowers Skipped", format: "eventCount" }
        ]
    },
    "GolfSwing": { // AG Complete
        id: "GolfSwing",
        name: "Golf Swing",
        type: "Movement",
        expectedGoals: [
            { key: "Swings", label: "Swings" },
            { key: "Perfect Swings", label: "Perfect Swings" }
        ],
        expectedEvents: [
            { key: "AFFECTED_ARM", label: "Hand Chosen", format: "eventValueFirst" }
        ]
    },
    "GradedMotorImagery": { // AG Complete
        id: "GradedMotorImagery",
        name: "Graded Motor Imagery",
        type: "Meditation"
    },
    "Gratitude": { // AG Complete
        id: "Gratitude",
        name: "Gratitude",
        type: "Meditation",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "GroceryShopping": { // Verify
        id: "GroceryShopping",
        name: "Grocery Shopping",
        type: "Movement",
        expectedRoms: [],
        expectedGoals: [
            { key: "Shopping List Buys", label: "Shopping List Buys" }, // Missing from AG
            { key: "Premium List Buys", label: "Premium List Buys" }, // Missing from AG
            { key: "Hidden Item Buys", label: "Hidden Item Buys" }, // Missing from AG
            { key: "Budget Bonus", label: "Budget Bonus" } // Missing from AG
        ],
        expectedEvents: [
            { key: "GroceryShopping__BUDGET_CONTROL_ENABLED", label: "Budget Control", format: "eventEnabled" },
            { key: "GroceryShopping__DISCOUNTS_ENABLED", label: "Discounts", format: "eventEnabled" },
            { key: "GroceryShopping__HIDDEN_ITEMS_ENABLED", label: "Hidden Items", format: "eventEnabled" },
            { key: "GroceryShopping__DISTRACTIONS_ENABLED", label: "Distractions", format: "eventEnabled" },
            { key: "GroceryShopping__SHELF_SHUFFLE_ENABLED", label: "Shelf Shuffle", format: "eventEnabled" },
            { key: "GroceryShopping__MAX_ACHIEVABLE_SCORE", label: "Max Achievable Score", format: "eventValue" }
        ]
    },
    "Grounding": { // AG Complete
        id: "Grounding",
        name: "Grounding",
        type: "Meditation"
    },
    "GuidedImagery": { // AG Complete
        id: "GuidedImagery",
        name: "Guided Imagery Mountain",
        type: "Meditation",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "GuidedImageryBeach": { // AG Complete
        id: "GuidedImageryBeach",
        name: "Guided Imagery Beach",
        type: "Meditation",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "GuidedMeditation": { // AG Complete
        id: "GuidedMeditation",
        name: "Guided Meditation",
        type: "Meditation"
    },
    "LeftRightDiscrimination": { // AG Complete
        id: "LeftRightDiscrimination",
        name: "Left/Right Discrimination",
        type: "Movement",
        expectedGoals: [
            { key: "Basic Sets Completed", label: "Basic Sets Completed" },
            { key: "Intermediate Sets Completed", label: "Intermediate Sets Completed" },
            { key: "Advanced Sets Completed", label: "Advanced Sets Completed" }
        ],
        expectedEvents: [
            { key: "LeftRightDiscrimination__SET_1_GUESS_ACCURACY", label: "Guess Accuracy (Set 1)", format: "eventPercentage" },
            { key: "LeftRightDiscrimination__SET_2_GUESS_ACCURACY", label: "Guess Accuracy (Set 2)", format: "eventPercentage" },
            { key: "LeftRightDiscrimination__SET_3_GUESS_ACCURACY", label: "Guess Accuracy (Set 3)", format: "eventPercentage" },
            { key: "LeftRightDiscrimination__BASIC_AVG_DECISION_TIME_LEFT", label: "Avg Basic Decision Time (Left)", format: "eventSeconds" },
            { key: "LeftRightDiscrimination__BASIC_AVG_DECISION_TIME_RIGHT", label: "Avg Basic Decision Time (Right)", format: "eventSeconds" },
            { key: "LeftRightDiscrimination__INTERMEDIATE_AVG_DECISION_TIME_LEFT", label: "Avg Intermediate Decision Time (Left)", format: "eventSeconds" },
            { key: "LeftRightDiscrimination__INTERMEDIATE_AVG_DECISION_TIME_RIGHT", label: "Avg Intermediate Decision Time (Right)", format: "eventSeconds" },
            { key: "LeftRightDiscrimination__ADVANCED_AVG_DECISION_TIME_LEFT", label: "Avg Advanced Decision Time (Left)", format: "eventSeconds" },
            { key: "LeftRightDiscrimination__ADVANCED_AVG_DECISION_TIME_RIGHT", label: "Avg Advanced Decision Time (Right)", format: "eventSeconds" }
        ]
    },
    "LettingGo": { // AG Complete
        id: "LettingGo",
        name: "Letting Go",
        type: "Meditation"
    },
    "LotusToss": { // AG Complete
        id: "LotusToss",
        name: "Lotus Toss",
        type: "Movement",
        expectedGoals: [
            { key: "Flowers Tossed", label: "Flowers Tossed" }
        ],
        expectedEvents: [
            { key: "SKIP", label: "Flowers Skipped", format: "eventCount" }
        ]
    },
    "MatchThree": { // AG Complete
        id: "MatchThree",
        name: "Match Three",
        type: "Movement",
        expectedGoals: [
            { key: "Matches", label: "Matches" }
        ],
        expectedEvents: [
            // Only dispatched when full motion data capture is turned on due to the high frequency of them.
            // Don't expect them by default. They will appear in the Additional Events section if present.
            // { key: "MatchThree__PIECE_TOUCHED", label: "Gems Touched", format: "eventCount" }
        ]
    },
    "Mirroring": { // AG Complete
        id: "Mirroring",
        name: "Mirroring",
        type: "Movement",
        expectedGoals: [
            { key: "Target Hit", label: "Targets Hit" }
        ],
        expectedEvents: [
            { key: "AFFECTED_ARM", label: "Affected Arm", format: "eventValueFirst" }
        ]
    },
    "MuscleRelaxation": { // AG Complete
        id: "MuscleRelaxation",
        name: "Progressive Muscle Relaxation",
        type: "Meditation",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "NeckCalibration": { // AG Complete
        id: "NeckCalibration",
        name: "Catching Fireflies",
        type: "Movement",
        expectedRoms: [
            { key: "NeckExtension", label: "Neck Extension" },
            { key: "NeckFlexion", label: "Neck Flexion" },
            { key: "NeckLeftRotation", label: "Neck Left Rotation" },
            { key: "NeckRightRotation", label: "Neck Right Rotation" }
        ],
        expectedGoals: [
            { key: "Fireflies Collected", label: "Fireflies Collected" },
            { key: "Improved Ranges", label: "Improved Ranges" }
        ],
        expectedEvents: [
            { key: "NeckCalibration__FIREFLIES_SKIPPED", label: "Fireflies Skipped", format: "eventCount" }
        ],
        ignoredEvents: [
            "SKIP" // Handled by NeckCalibration__FIREFLIES_SKIPPED event
        ]
    },
    "PaintingPuzzle": { // AG Complete
        id: "PaintingPuzzle",
        name: "Painting Puzzle",
        type: "Movement",
        expectedGoals: [
            { key: "Puzzles Completed", label: "Puzzles Completed" },
            { key: "Pieces Placed", label: "Pieces Placed" }
        ],
        expectedEvents: [
            { key: "PaintingPuzzle__PIECES_RAISED", label: "Pieces Raised", format: "eventCount" }
        ]
    },
    "RestoringBalance": { // AG Complete
        id: "RestoringBalance",
        name: "Restoring Balance",
        type: "Meditation"
    },
    "Rewiring": { // AG Complete
        id: "Rewiring",
        name: "Rewiring",
        type: "Meditation",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "Ruling": { // Verify
        id: "Ruling",
        name: "Ruling In/Out",
        type: "Movement",
        expectedEvents: [
            { key: "RulingInOut__RESULTS", label: "Ruling Results", format: "rulingResultsFormatter" }
        ]
    },
    "SomaticPainting": { // AG Complete
        id: "SomaticPainting",
        name: "Somatic Painting",
        type: "Meditation"
    },
    "SomaticTracking": { // AG Complete
        id: "SomaticTracking",
        name: "Somatic Tracking",
        type: "Meditation"
    },
    "SomaticTrackingExtended": { // AG Complete
        id: "SomaticTrackingExtended",
        name: "Somatic Tracking Extended",
        type: "Meditation"
    },
    "StarryNight": { // AG Complete
        id: "StarryNight",
        name: "Starry Night",
        type: "Movement",
        expectedGoals: [
            { key: "Shapes Completed", label: "Shapes Completed" }
        ],
        expectedEvents: [
            { key: "AFFECTED_ARM", label: "Affected Arm", format: "eventValueFirst" }
        ]
    },
    "Stargazing": { // AG Complete
        id: "Stargazing",
        name: "Star Gazing Meditation",
        type: "Meditation",
        expectedEvents: [
            { key: "POSE", label: "Position", format: "eventValueFirst" }
        ]
    },
    "Tutorial": { // AG Complete
        id: "Tutorial",
        name: "Tutorial",
        type: "Coaching"
    },
    "UnloadDishwasher": { // AG Complete
        id: "UnloadDishwasher",
        name: "Unload Dishwasher",
        type: "Movement",
        expectedGoals: [
            { key: "Dishes Unloaded", label: "Dishes Stacked" }
        ],
        expectedEvents: [
            { key: "SKIP", label: "Dishes Skipped", format: "eventCount" }
        ]
    },
    "Vacuuming": { // AG Complete
        id: "Vacuuming",
        name: "Vacuuming",
        type: "Movement",
        expectedGoals: [
            { key: "Carpets Cleaned", label: "Carpets Cleaned" }
        ]
    }
};

export const getActivityDefinition = (activityId: string): ActivityDefinition | null => {
    return ACTIVITY_DEFINITIONS[activityId] || null;
};